import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useSandboxItems } from "@/hooks";
import { ExtraAttachmentsInput } from "./_partials/ExtraAttachmentsInput";
import {
  type InferredModalAddExtraAttachmentSchema,
  modalAddExtraAttachmentFormSchema,
} from "./ModalAddExtraAttachment.schema";
import { type ModalAddExtraAttachmentFormProps } from "./ModalAddExtraAttachment.types";

export const ModalAddExtraAttachmentForm = ({
  isOpen,
  setOpenState,
  sandboxItemId,
  orderId,
  orderItemId,
  addToast,
}: ModalAddExtraAttachmentFormProps) => {
  const { t } = useTranslate();

  // Hooks
  const { usePatchSandboxItem } = useSandboxItems();
  const { mutateAsync: patchSandboxItem, isPending } = usePatchSandboxItem();

  // Form
  const methods = useForm<InferredModalAddExtraAttachmentSchema>({
    defaultValues: {
      extraAttachments: [{ fileSignedId: undefined }],
    },
    resolver: zodResolver(modalAddExtraAttachmentFormSchema),
  });

  const onSubmit: SubmitHandler<InferredModalAddExtraAttachmentSchema> = async (
    data
  ) => {
    const res = await patchSandboxItem({
      orderId,
      orderItemId: orderItemId,
      sandboxItemId: sandboxItemId,
      sandboxItemUpdateCommand: {
        extraAttachments: data.extraAttachments.filter((a) => !!a.fileSignedId),
      },
    });

    if (res.status === 200 || res.status === 201) {
      addToast({
        state: "success",
        title: t("order.myOrdersItemDetails.extraAttachmentsModal.toastSucces"),
      });
    } else {
      addToast({
        state: "error",
        title: t("toast.generic.error"),
      });
    }

    setOpenState(false);
  };

  const onError = () => {
    addToast({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  return (
    <FileContextProvider>
      <FormProvider {...methods}>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              isOpen={isOpen}
              onOpenChange={() => setOpenState(!isOpen)}
              dialogProps={{
                overflow: true,
                dialogHeaderProps: {
                  action: () => setOpenState(false),
                  title: t(
                    "order.myOrdersItemDetails.extraAttachmentsModal.title"
                  ),
                  description: t(
                    "order.myOrdersItemDetails.extraAttachmentsModal.subtitle"
                  ),
                },
                content: <ExtraAttachmentsInput />,
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      variant: "outlined",
                      color: "neutral",
                      isLoading: isPending,
                      disabled: isPending || ctx.isUploading,
                      onPress: () => setOpenState(false),
                    },
                    {
                      children: t("buttons.validate"),
                      type: "submit",
                      isLoading: isPending,
                      disabled: isPending || ctx.isUploading,
                      onPress: () =>
                        void methods.handleSubmit(onSubmit, onError)(),
                    },
                  ],
                  type: "double",
                },
              }}
            />
          )}
        </FileContext.Consumer>
      </FormProvider>
    </FileContextProvider>
  );
};
